import { AffiliateLogos, ContactBanner, Container, ContinueModuleCard, GoalsCard, InformationBanner, ModuleCard, Page, Spotlight, SPOTLIGHT_ITEMS, WelcomeVideoDialog } from "@/components";
import { CONSTANTS } from "@/constants";
import { IGetUserFlowByUrlUserSteps, useAddCustomEvent, useGetModules, useGroups } from "@/hooks";
import { capitalise, isModuleOpen, getParentFeedbackConstants, getParentModuleConstants, getSummaryByModule } from "@/utils";
import { useToast } from "@netfront/ekardo-content-library";
import { useDomain } from "@netfront/gelada-identity-library";
import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Button, Dialog, Skeleton } from "@netfront/ui-library";
import { deleteCookie, getCookieValue } from "@netfront/common-library";
import { CallBackProps } from "react-joyride";
import { useGoogleAnalytics } from "@netfront/ekardo-content-library";
import { EAnalyticsEvent } from "@/interfaces/enums";
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from "@netfront/apollo-providers";
import { CustomFieldResponseBooleanGraphType, CustomFieldResponseInterfaceGraphType, CustomFieldResponseMutation, CustomFieldResponseMutationAnswerCustomFieldsArgs, UserQuery } from "@netfront/api-gelada-private-types";
import { ANSWER_CUSTOM_FIELDS_MUTATION } from "@/graphql/mutations";

export const ParentDashboard = () => {
  const { CUSTOM_FIELDS } = CONSTANTS;
  const { getDomain, isDomainReady } = useDomain();
  const { hasFeedback } = useGroups();
  const { handleToastError } = useToast();

  const { trackEvent } = useGoogleAnalytics();

  const [shouldShowSpolightTour, setShouldShowSpolightTour] = useState<boolean>(false);
  const [spotLightData, setSpotLightData] = useState<CallBackProps>();
  const [shouldShowModuleCompletionDialog, setShouldShowModuleCompletionDialog] = useState<boolean>(Boolean(getCookieValue(CONSTANTS.COOKIES.MODULE_COMPLETE)));
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState<boolean>(false);
  const [isSpotlightPending, setIsSpotlightPending] = useState<boolean>(false);

  const { handleMutation: answerCustomFields } = useMutation<Pick<CustomFieldResponseMutation, 'answerCustomFields'>, CustomFieldResponseMutationAnswerCustomFieldsArgs>({
    apiName: 'gelada',
    mutation: ANSWER_CUSTOM_FIELDS_MUTATION
  })

  const { handleQuery: handleGetLoggedUser } = useLazyQuery<{ user: Pick<UserQuery, 'getLoggedUser'> }>({
      apiName: 'gelada',
          query: gql`
      query {
        user {
          getLoggedUser {
            id
            customFields {
              customFieldId
              ... on CustomFieldResponseBooleanGraphType {
                value
              }
            }
          }
        }
      }
    `,
    onCompleted: ({ user: { getLoggedUser } }) => {
      if(!getLoggedUser) return;

      const { customFields } = getLoggedUser;

      const isVideoWatched = customFields
        ?.filter((field): field is CustomFieldResponseInterfaceGraphType  => Boolean(field))
        ?.find((field): field is CustomFieldResponseBooleanGraphType => field.customFieldId === CUSTOM_FIELDS.VIDEO_WATCHED.ID)?.value ?? false;

      if(!isVideoWatched) {
        setIsVideoDialogOpen(true);
      }
    }
  });

  const { modules, isLoading: isModulesLoading, feedbackSteps, summaryModules } = useGetModules({
    query: gql`
      query getUserFlowByUrl($projectId: String!, $url: String!) {
        userFlow {
          getUserFlowByUrl(projectId: $projectId, url: $url) {
            userFlowSteps {
              contentGroup {
                linkedAssets {
                  title
                  presignedUrl
                  tagList {
                    name
                  }
                }
                iconAsset {
                  presignedUrl
                }
                contentPages(status: [PUBLISHED]) {
                  url
                  id
                  parentId
                  sort
                }
                sort
                title
              }
              id
              isComplete
              lastTracking {
                startedLongDate
              }
              lastViewedContentPageCompletion {
                contentPage {
                  url
                }
              }
              percentageCompleted
              sort
              stepName
              url
            }
          }
        }
      }
    `
  });


  const { handleAddCustomEvent } = useAddCustomEvent({
    onError: error => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true
      })
    }
  });


  const latestModule = modules.find(({ isComplete }) => !isComplete);

  const trackBookHeathCoachClick = () => {
    handleAddCustomEvent({ 
      projectId: String(process.env.REACT_APP_PROJECT_ID), 
      type: 'book-health-coach' 
    });
    trackEvent(EAnalyticsEvent.BookHealthCoachClick);
  };
  
  const getModuleLink = (step: IGetUserFlowByUrlUserSteps ) => {
    const firstPageUrl = step?.contentGroup?.contentPages.find(({ parentId, sort }) => parentId === null && sort === 0)?.url;
    const moduleLink = `/${CONSTANTS.USERFLOW.PARENT.URL}/${step.url}/${
      step.isComplete
        ? firstPageUrl
        : step.lastViewedContentPageCompletion
          ? step.lastViewedContentPageCompletion.contentPage.url
          : firstPageUrl
    }`;

    const feedbackStep = feedbackSteps.find(({ id }) => step.id === getParentFeedbackConstants(id)?.MODULE.ID);

    const feedbackLink = `/feedback/${
      feedbackStep?.url
    }/${feedbackStep?.contentGroup?.contentPages[0].url}`;

    const isStressManagementModule = step.id === CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.COPING_WITH_STRESS.ID;

    if(step.isComplete) {
      return moduleLink
    }
    
    return hasFeedback ? feedbackStep?.isComplete || isStressManagementModule ? moduleLink : feedbackLink : moduleLink;
  };


  const hasCompletedModules = modules.length > 0 && !modules.some(({ isComplete }) => !isComplete);
  const hasStartedAnyModule = modules.some(({ lastViewedContentPageCompletion }) => lastViewedContentPageCompletion);

  useEffect(() => {
    handleGetLoggedUser({});
  }, []);

  useEffect(() => {
    if(!spotLightData) {
      setShouldShowSpolightTour(false)
    }

    if(spotLightData?.status === 'ready') {
      setShouldShowSpolightTour(false)
    }
  }, [spotLightData]);

  useEffect(() => {
    if(!isDomainReady) return;

    if(getCookieValue(CONSTANTS.COOKIES.SPOTLIGHT)) {
      setIsSpotlightPending(true);
      deleteCookie(CONSTANTS.COOKIES.SPOTLIGHT, {
        domain: getDomain(),
        path: '/'
      })
    }
  }, [isDomainReady]);

  useEffect(() => {
    if(isSpotlightPending && !isVideoDialogOpen) {
      setShouldShowSpolightTour(true);
      setIsSpotlightPending(false);
    }
  }, [isSpotlightPending, isVideoDialogOpen]);

  useEffect(() => {
    if(shouldShowSpolightTour) {
      trackEvent(EAnalyticsEvent.TakeSpotlightTour);
    }
  }, [shouldShowSpolightTour]);


  return(
    <Page
      customCMSPageEventType="parent-dashboard"
      hasPrivateLayout
      meta={{
        seoDescription: 'Access modules and goal setting features',
        seoTitle: 'Dashboard',
      }}
      title='Dashboard'
    >     
      {shouldShowSpolightTour && (
        <Spotlight
          disableCloseOnEsc={true}
          hideCloseButton={true}
          spotlightItem={SPOTLIGHT_ITEMS}
          onSpotLightData={setSpotLightData}
        />
      )}

      <WelcomeVideoDialog 
        isOpen={isVideoDialogOpen} 
        onClose={async () => {
          try {
            await answerCustomFields({
              projectId: String(process.env.REACT_APP_PROJECT_ID),
              reponses: [{ customFieldId: CUSTOM_FIELDS.VIDEO_WATCHED.ID, value: 'true' }],
            });

          } catch(error) {
            console.error(error);
          } finally {
            setIsVideoDialogOpen(false);
          }
        }} 
      />
      
      <Dialog
        isNarrow
        isOpen={shouldShowModuleCompletionDialog}
        title="Congratulations!"
        confirmText="Close"
        onConfirm={() => {
          deleteCookie(CONSTANTS.COOKIES.MODULE_COMPLETE, {
            domain: getDomain(),
            path: '/'
          });

          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
          });

          setShouldShowModuleCompletionDialog(false);
        }}
      >
        <img alt="Trophy" className="mb-4" src='/images/achievement-icon.svg' />
        <p className="text-xl mb-4">
          Well done, you have now completed the {capitalise(String(getCookieValue(CONSTANTS.COOKIES.MODULE_COMPLETE)))} module. Your summary will be on your dashboard and sent via email. 
        </p>

        <InformationBanner additionalClassNames="my-6 bg-grey-300 border-none shadow-none">
          <p className="text-left"><strong>Tip:</strong> Make time in your diary to complete your next module (e.g., one week from today)!</p>
        </InformationBanner>
      </Dialog>

      {hasStartedAnyModule && (
        <section className="py-8 md:py-16">
          <Container>
            <div className="bg-white border border-grey-300 shadow-xl rounded-lg p-8">
              <div className="flex flex-col lg:flex-row items-start mb-6">
                <div className="flex-1">
                  <span className="text-xl weight-800 color-green-500 text-uppercase">Quick access</span>
                  <h1 className="text-4xl weight-800 color-black text-uppercase mb-4">Dashboard</h1>

                  <div className="flex flex-col items-start md:flex-row md:items-center w-full md:w-1/2">
                    <img className="mr-4 mb-4 lg:mb-0" src="/images/hat-icon.svg" alt="Info" />
                    <p>Quickly access where you last exited the program and your most recent goals.</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4 lg:mt-0">
                  <Button
                    additionalClassNames="no-kanzi-pdf"
                    text='Intro video'
                    onClick={() => setIsVideoDialogOpen(true)}
                  />

                  <Button
                    additionalClassNames="no-kanzi-pdf"
                    text='Take a tour'
                    onClick={() => setShouldShowSpolightTour(true)}
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex-1">
                  <ContinueModuleCard
                    {...!hasCompletedModules && latestModule && {
                      action: {
                        linkButton: { 
                          id: 'continue-module', 
                          url: getModuleLink(latestModule)
                        },
                        variant: 'tertiary',
                        text: `${Math.round(Number(latestModule?.percentageCompleted) * 100) === 0 ? 'Begin' : 'Continue'}`
                      }
                    }}
                    subTitle={hasCompletedModules ? 'You have completed all modules' : Math.round(Number(latestModule?.percentageCompleted) * 100) === 0 ? "Get started" : "Continue where you left off"}
                    isCompleted={hasCompletedModules}
                    isLoading={isModulesLoading}
                    percentageComplete={Math.round((latestModule?.percentageCompleted ?? 0) * 100)}
                    title={hasCompletedModules ? 'Congratulations!' : latestModule?.contentGroup.title} 
                  />
                </div>
    
                <div className="flex-1">
                  <GoalsCard />
                </div>
              </div>
            </div>
          </Container>
        </section>
      )}

      <section className="bg-grey-300 py-16">
        <Container>
          <div className="flex flex-col md:flex-row items-start mb-8">
            <div className="flex-1">
              <span className="text-xl weight-800 color-green-500 text-uppercase">Explore</span>
              <h2 className="text-4xl weight-800 color-black text-uppercase mb-2">The Health4Life Parents & Teens Modules</h2>
              <p className="mb-0">Review previous modules and access new modules.</p>
            </div>

            {!hasStartedAnyModule && (
              <Button
                additionalClassNames="no-kanzi-pdf mt-4 lg:mt-0"
                text='Take a tour'
                onClick={() => setShouldShowSpolightTour(true)}
              />
            )}
          </div>

          {isModulesLoading ? (
            <>
              <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                  <Skeleton borderRadius='20px' height='20rem' />
                </div>
                <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                  <Skeleton borderRadius='20px' height='20rem' />
                </div>
                <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                  <Skeleton borderRadius='20px' height='20rem' />
                </div>
                <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                  <Skeleton borderRadius='20px' height='20rem' />
                </div>
              </div>
            </>
          ) : (
            <ol className="flex justify-center flex-wrap -mx-2 list-style-none c-spotlight--modules">
              {modules && modules.length ? modules.map((step, index) => {
                const { contentGroup, id: stepId, isComplete, percentageCompleted } = step;

                const summaryStep = getSummaryByModule(summaryModules, stepId);
                
                return(
                  <li key={stepId} className="w-full md:w-1/2 lg:w-1/4 p-2 m-0">
                    <ModuleCard 
                      additionalClassNames={`bg-${getParentModuleConstants(stepId)?.THEME}-300`}
                      href={getModuleLink(step)}
                      icon={contentGroup.iconAsset?.presignedUrl}
                      percentageComplete={Math.round(percentageCompleted * 100)}
                      progressIndicator={{
                        path: {
                          stroke: `var(--${getParentModuleConstants(stepId)?.THEME}-400)`,
                        }
                      }} 
                      subTitle={`Module ${index + 1}`}
                      title={contentGroup.title}
                      {...isComplete && {
                        summary: `/summaries/${summaryStep?.url}/${summaryStep?.contentGroup?.contentPages[0].url}`
                      }}
                    />
                  </li>
                )
              }) : null}
            </ol>
          )}

          
        </Container>
      </section>

      <Container>
        <div className="flex flex-col py-16">
          <ContactBanner />
          <AffiliateLogos />
        </div>
      </Container>

      <div className="bg-lime-gradient py-1" />
    </Page>
  )
};